import { container } from "tsyringe";

import { ERROR_CODES, PROMISE_TIMEOUT_MS } from "./constants";
import { SdkEventInterface } from "./events/SdkEventInterface";
import SdkError from "./methods/SdkError";
import { SdkMethodPayloadInterface } from "./methods/SdkMethodPayloadInterface";
import { SdkModule } from "./SdkModule";
import { ErrorResult } from "./types";

interface CustomSubscription {
    unsubscribe: () => void;
}

export function subscribeToSdkMethod<
    T extends SdkMethodPayloadInterface = never
>(
    methodName: T["methodName"],
    callback: (payload: T) => void
): CustomSubscription {
    const sdkMethodsCollector =
        container.resolve(SdkModule).exports.sdkMethodsCollector;
    sdkMethodsCollector.addSubscriber<T>(methodName, callback);

    return {
        unsubscribe() {
            sdkMethodsCollector.deleteSubscriber(methodName, callback);
        },
    };
}

export function checkSubscribers(
    methodName: string,
    reject: (error: ErrorResult) => void
) {
    const sdkMethodsCollector =
        container.resolve(SdkModule).exports.sdkMethodsCollector;
    if (sdkMethodsCollector.hasSubscribers(methodName)) return;

    reject(
        new SdkError(
            ERROR_CODES.NO_SUBSCRIBERS,
            "Method execution failed: no subscribers found"
        )
    );
}

export function dispatchSdkEvent(event: SdkEventInterface) {
    container.resolve(SdkModule).exports.sdk.dispatch(event);
}

export function promiseWithTimeout<T>(
    promise: Promise<T>,
    timeoutMs: number = PROMISE_TIMEOUT_MS
) {
    let timeout: ReturnType<typeof setTimeout>;

    const timeoutPromise = new Promise<ErrorResult>((resolve, reject) => {
        timeout = setTimeout(() => {
            reject(
                new SdkError(
                    ERROR_CODES.TIMEOUT_ERROR,
                    `The operation did not complete within ${
                        timeoutMs / 1000
                    } seconds. Please try again.`
                )
            );
        }, timeoutMs);
    });

    return Promise.race([promise, timeoutPromise])
        .then((result) => {
            clearTimeout(timeout);
            return result;
        })
        .catch((error) => {
            clearTimeout(timeout);
            throw error;
        });
}

export function checkRequiredArguments(
    methodName: string,
    requiredCount: number,
    presentCount: number,
    reject: (error: ErrorResult) => void
) {
    if (presentCount < requiredCount) {
        reject(
            new SdkError(
                ERROR_CODES.INSUFFICIENT_ARGS,
                `Failed to execute '${methodName}' on 'sdk': ${requiredCount} arguments required, but only ${presentCount} present.`
            )
        );
    }
}

/**
 * Ensures that the specified method is triggered only once per day.
 *
 * This function uses LocalStorage to track the last execution date of the method.
 * If the method is called again on the same day, it will not trigger the callback.
 * The callback will be triggered again on the next day.
 *
 * @param methodName - Name of the method being called
 * @param app - Class where the method is defined, "sdk" or "sdk.common.events"
 * @param callback - Callback function to execute if the method has not been triggered today
 */
export function ensureMethodCalledEventTriggeredOnceDaily(
    methodName: string,
    app: "sdk" | "sdk.common.events",
    callback: () => void
) {
    try {
        const today = new Date().toISOString().split("T")[0];
        const storageKey = `sdk_method_called_${app}_${methodName}`;

        const lastTriggeredDate = localStorage.getItem(storageKey);

        if (lastTriggeredDate === today) {
            return;
        }

        localStorage.setItem(storageKey, today);
        callback();
    } catch {
        callback();
    }
}
